import {put, call} from 'redux-saga/effects';
import Answer from "../../entities/answer";
import * as Actions from "../actions/answerActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type AnswerAction = {
    answerId: string;
    answer: Answer;
    answers: Answer[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneAnswer(action: AnswerAction) {
	const session = tabSession.captureGeneration();
	try {
		const answer: Answer = yield call(api.findOne, 'answer', action.answerId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ANSWER_FETCH_ONE_SUCCESS, answerId: action.answerId, answer});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ANSWER_FETCH_ONE_FAILURE, answerId: action.answerId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {AnswerAction} action
 */
export function* saveAnswer(action: AnswerAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ ANSWER_SAVE saga', action.answer?.id);
    
	if (action.answer === undefined || action.answer === null) {
		console.error('saveAnswer received a null');
		throw new Error('saveAnswer received a null');
	}

	if (!(action.answer instanceof Answer)) {
		action.answer = new Answer(action.answer);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'answer', action.answer);
		if (addResult.isErr()) {
			console.error(`⮂ saveAnswer: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.ANSWER_SAVE_FAILURE, answer: action.answer, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ANSWER_SAVE, answer: action.answer});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.ANSWER_SAVE_FAILURE, answer: action.answer, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {AnswerAction} action
 * @param {Answer[]} action.answers
 */
export function* saveManyAnswers(action: AnswerAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ ANSWER_SAVE_MANY saga', action.answers.map(answer => answer.id).join(','));
    
    if (!Array.isArray(action.answers)) {
        console.error('saveManyAnswers expected an array, received', action.answers);
        throw new Error('saveManyAnswers expected an array');
    }

	try {
		const toSave: Answer[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.answers.length; i++) {
			const answer = action.answers[i];
			answer.persisted = false;
			toSave.push(answer);
		}

		const addResult = yield call(idb.add, 'answer', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyAnswers: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.ANSWER_SAVE_MANY_FAILURE, answers: action.answers, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ANSWER_SAVE_MANY, answers: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.ANSWER_SAVE_MANY_FAILURE, answers: action.answers, status: 500, err});

		console.error(`Error saving ${action.answers.length} answers to database`, err);
	}
}

/**
 * @param {AnswerAction} action
 */
export function* removeAnswer(action: AnswerAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.answer === undefined || action.answer === null || !(action.answer instanceof Answer)) {
			console.error('removeAnswer expected type Answer, received', action.answer);
			throw new Error('removeAnswer expected type Answer');
		}

	try {
		// Mark as deleted
		action.answer.status = 'D';
		action.answer.addedOn = newHRTimestamp();
		action.answer.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'answer', action.answer);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeAnswer: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ANSWER_REMOVE_FAILURE, answer: action.answer, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ANSWER_REMOVE, answer: action.answer});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ANSWER_REMOVE_FAILURE, answer: action.answer, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Answer.
	*
	* @param {AnswerAction} action
	*/
export function* restoreAnswer(action: AnswerAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreAnswer expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.answer === undefined || action.answer === null) {
        console.error('restoreAnswer expected a Answer, received null');
        throw new Error('restoreAnswer expected a Answer, received null');
    }
    else if (!(action.answer instanceof Answer)) action.answer = new Answer(action.answer); 
    
	try {
		// Mark as restored
		action.answer.status = 'U';
		action.answer.addedOn = newHRTimestamp();
		action.answer.addedBy = userId;
		action.answer.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'answer', action.answer);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreAnswer: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ANSWER_RESTORE_FAILURE, answer: action.answer, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ANSWER_RESTORE, answer: action.answer});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ANSWER_RESTORE_FAILURE, answer: action.answer, status: 500, err});

		console.error('Error restoring from database', err);
	}
}